/**
 * Roles: what somebody is trained and trusted to do (WALLIYO-0131).
 *
 * A screen of its own, not a tab beside Teams, because the two are not the
 * same authority. Arranging who works on what is MANAGE_TEAMS, which is high;
 * deciding what anybody may do is MANAGE_ROLES, which is critical. A screen
 * can only be gated on one feature, so they are two screens.
 *
 * A role is the same wherever somebody sits, so there are no manager roles
 * here: leading a team is a fact on the membership, over on Teams. That is
 * what keeps this list from growing an Engineering Manager, an HR Manager and
 * a Payments Manager, each with near-identical permissions.
 *
 * What a role *grants* is not here either. That is Permissions and Feature
 * Enablements, which already own it; a second grant editor would mean two
 * screens writing the same rows by different paths.
 */

import type { StaffRoleListItem } from "@wallyo/contracts/console";
import { useState } from "react";

import { createRole, editRole, listRoles, listStaff } from "../../api/console-api";
import { useApi } from "../../api/context";
import { categoryName, featureName, permissionName, t } from "../../i18n";
import { RolePanel, RoleRail, RolesNotice } from "./RolesList";
import { inReachOrder, overview } from "./roles-summary";
import { GiveRoleDialog, TakeRoleDialog } from "./RoleDialogs";
import type { StaffMemberListItem } from "@wallyo/contracts/console";
import { DEPARTMENT_IDS, type DepartmentId } from "@wallyo/contracts/workspaces";

import { workspaceLabel } from "../../navigation/screens";

import { useAlerts } from "../../shell/alerts";
import { PageTop } from "../../shell/PageTop";
import { useStaff } from "../../shell/staff-context";
import { LoadFailed, Loading, useLoad } from "../config-parts";
import { describeConfigFailure } from "../config-refusal";
import { WriteFailureNotice, useIntentWrite } from "../team/TeamDialogs";
import type { CellLock, GridFeature, GridPermission, PermissionLock } from "../team/team-access";
import {
  EMPTY_ROLE_DRAFT,
  createProblem,
  createRequest,
  groups,
  hasFeature,
  hasPermission,
  lockOf,
  opensScreens,
  permissionLockOf,
  permissionsOf,
  withFeature,
  withPermission,
  type RoleDraft,
} from "./roles-model";

/** Creating and renaming a role needs the same feature as granting on one. */
const MANAGE_ROLES = "MANAGE_ROLES_STAFF_FEATURE_ENABLEMENT";

const describeFailure = (error: unknown) => describeConfigFailure(error, {}, "features.stale");

/**
 * The roles: what each one is, how many people hold it, and the form that
 * makes one.
 *
 * What a role *grants* is not here. That is Permissions and Feature
 * Enablements, which already own it; putting a second grant editor here would
 * mean two screens writing the same rows by different paths.
 */
export function RolesScreen() {
  const { client } = useApi();
  const { me } = useStaff();
  const [writes, setWrites] = useState(0);
  const [making, setMaking] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [comparedId, setComparedId] = useState<string>("");
  const [giving, setGiving] = useState(false);
  const [taking, setTaking] = useState<StaffMemberListItem | null>(null);
  const alerts = useAlerts();
  const list = useLoad(() => listRoles(client), `roles#${writes}`);
  // Who holds what, for the block that manages it and for the band: somebody
  // holding two critical roles is one person, and adding member counts says two.
  const people = useLoad(() => listStaff(client), `roles-staff#${writes}`);
  const staff = people.state.data?.staffMembers ?? [];
  // A failed read is not an empty company. Everything that counts people
  // says so instead of showing nought.
  const holdersKnown = people.state.data !== null;
  const roles = inReachOrder(list.state.data?.roles ?? []);
  const editable = (me.features as readonly string[]).includes(MANAGE_ROLES);
  // The first role, until somebody picks another. A read after a write keeps
  // the pick, because the id survives the reload even though the object does
  // not.
  const selected = roles.find((role) => role.staffRoleId === selectedId) ?? roles[0] ?? null;

  if (list.state.status === "failed") {
    return (
      <LoadFailed
        message={t("roles.loadFailed")}
        retry={t("roles.retry")}
        error={list.state.error}
        onRetry={list.reload}
      />
    );
  }
  if (!list.state.data) return <Loading text={t("roles.loading")} />;
  if (!selected && roles.length > 0) return <Loading text={t("roles.loading")} />;

  // The landing is the list. Creating is behind a button, because you arrive
  // here far more often to read or change a role than to make one.
  if (making) {
    return (
      <>
        <PageTop title={t("roles.new.title")} />
        <div className="subject-bar">
          <button type="button" className="btn quiet small" onClick={() => setMaking(false)}>
            {t("roles.back")}
          </button>
        </div>
        <NewRole
          onCreated={() => {
            setMaking(false);
            setWrites((count) => count + 1);
          }}
        />
      </>
    );
  }

  return (
    <>
      <PageTop title={t("roles.title")} sub={t("roles.explain")} />
      <div className="subject-bar">
        <RolesNotice overview={overview(roles, staff)} holdersKnown={holdersKnown} />
        <div style={{ flexGrow: 1 }} />
        {editable ? (
          <button type="button" className="btn primary" onClick={() => setMaking(true)}>
            {t("roles.new")}
          </button>
        ) : null}
      </div>
      {roles.length === 0 ? (
        <div className="empty">{t("roles.none")}</div>
      ) : (
        <div className="roles-layout">
          <RoleRail roles={roles} selectedId={selected.staffRoleId} onSelect={setSelectedId} />
          {editing ? (
            <RoleEditor
              role={selected}
              onCancel={() => setEditing(false)}
              onSaved={() => {
                setEditing(false);
                setWrites((count) => count + 1);
              }}
            />
          ) : (
            <RolePanel
              role={selected}
              holdersKnown={holdersKnown}
              holders={staff.filter((member) => member.roles.some((each) => each.staffRoleId === selected.staffRoleId))}
              others={roles.filter((role) => role.staffRoleId !== selected.staffRoleId)}
              comparedWith={roles.find((role) => role.staffRoleId === comparedId) ?? null}
              editable={editable}
              opens={opensOf(selected)}
              onEdit={() => setEditing(true)}
              onGive={() => setGiving(true)}
              onTake={setTaking}
              onCompare={setComparedId}
            />
          )}
        </div>
      )}
      {giving && selected ? (
        <GiveRoleDialog
          role={selected}
          candidates={staff.filter(
            (member) =>
              member.status !== "deactivated" &&
              !member.roles.some((each) => each.staffRoleId === selected.staffRoleId),
          )}
          onClose={() => setGiving(false)}
          onReload={() => {
            setGiving(false);
            setWrites((count) => count + 1);
          }}
          onDone={(message) => {
            alerts.ok(message);
            setGiving(false);
            setWrites((count) => count + 1);
          }}
        />
      ) : null}
      {taking && selected ? (
        <TakeRoleDialog
          role={selected}
          member={taking}
          onClose={() => setTaking(null)}
          onReload={() => {
            setTaking(null);
            setWrites((count) => count + 1);
          }}
          onDone={(message) => {
            alerts.ok(message);
            setTaking(null);
            setWrites((count) => count + 1);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The screens a role opens, named the way the menu names them.
 *
 * Computed by the same function the shell asks, so it cannot drift: a stored
 * list would be a second answer to a question that already has one.
 */
function opensOf(role: StaffRoleListItem): string[] {
  const draft: RoleDraft = {
    features: role.features.map((grant) => grant.settingId),
    permissions: role.permissions.map((grant) => grant.settingId),
  };
  return opensScreens(draft)
    .filter((each) => each.opens)
    .map((each) => t(each.screen.label));
}

/** Renaming a role, in place of the panel. */
function RoleEditor({
  role,
  onCancel,
  onSaved,
}: {
  role: StaffRoleListItem;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { client } = useApi();
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? "");
  const [reason, setReason] = useState("");
  const save = useIntentWrite();

  return (
    <section className="card role-panel">
      <h2>{t("roles.edit")}</h2>
      <div className="field">
        <label htmlFor="role-name">{t("roles.col.name")}</label>
        <input
          id="role-name"
          type="text"
          value={name}
          onChange={(event) => {
            save.edited();
            setName(event.target.value);
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="role-description">{t("roles.col.description")}</label>
        <input
          id="role-description"
          type="text"
          value={description}
          onChange={(event) => {
            save.edited();
            setDescription(event.target.value);
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="role-reason">{t("team.reason")}</label>
        <input
          id="role-reason"
          type="text"
          value={reason}
          onChange={(event) => {
            save.edited();
            setReason(event.target.value);
          }}
        />
      </div>
      {save.problem ? <p className="field-problem">{save.problem}</p> : null}
      {save.error ? <WriteFailureNotice failure={describeFailure(save.error)} onReload={onSaved} /> : null}
      <div className="btns">
        <button type="button" className="btn quiet" onClick={onCancel}>
          {t("roles.cancel")}
        </button>
        <button
          type="button"
          className="btn primary"
          disabled={save.pending}
          onClick={() =>
            void save.run(
              name.trim().length === 0 ? t("roles.new.nameNeeded") : null,
              (idempotencyKey) =>
                editRole(
                  client,
                  role.staffRoleId,
                  {
                    name: name.trim(),
                    description: description.trim() || null,
                    reason: reason.trim(),
                    version: role.version,
                  },
                  { idempotencyKey },
                ),
              onSaved,
            )
          }
        >
          {t("roles.save")}
        </button>
      </div>
    </section>
  );
}

/**
 * A new role, and what it is for.
 *
 * The grants come with it rather than after it: a role that grants nothing has
 * no effect anybody can see, and whoever is creating one is already deciding
 * what it should be able to do. The server writes the role and its grants in
 * one transaction, so there is no moment when the role is a name that means
 * nothing.
 *
 * Skills only. No Engineering Manager, no HR Manager — a role is the same
 * wherever somebody sits, and leading a team is a fact on the membership, over
 * on Teams.
 *
 * Nothing is offered that the person filling this in could not grant
 * afterwards: the same rule, asked here so the box is dead before it is
 * pressed rather than refused after.
 */
function NewRole({ onCreated }: { onCreated: () => void }) {
  const { client } = useApi();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [department, setDepartment] = useState<DepartmentId | "">("");
  const [reason, setReason] = useState("");
  const [draft, setDraft] = useState<RoleDraft>(EMPTY_ROLE_DRAFT);
  const [notice, setNotice] = useState<string | null>(null);
  const save = useIntentWrite();

  const submit = () => {
    save.edited();
    void save.run(
      createProblem(draft, name, department, reason),
      (idempotencyKey) =>
        createRole(client, createRequest(draft, name, description, department as DepartmentId, reason), {
          idempotencyKey,
        }),
      () => {
        setName("");
        setDescription("");
        setDepartment("");
        setReason("");
        setDraft(EMPTY_ROLE_DRAFT);
        setNotice(t("roles.created"));
        onCreated();
      },
    );
  };

  return (
    <form
      className="card"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="subject-bar">
        <label className="subject-uuid">
          <span className="subject-label">{t("roles.new.name")}</span>
          <input type="text" value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label className="subject-uuid">
          <span className="subject-label">{t("roles.new.description")}</span>
          <input type="text" value={description} onChange={(event) => setDescription(event.target.value)} />
        </label>
        {/* Which department it belongs to, and so which workspace its holders
            land in. Required: a QA tester is Product & Engineering, an
            accountant is Finance, and a role that says neither opens nothing
            (WALLIYO-0132). */}
        <label className="subject-uuid">
          <span className="subject-label">{t("roles.new.department")}</span>
          <select value={department} onChange={(event) => setDepartment(event.target.value as DepartmentId | "")}>
            <option value="">{t("roles.new.pickDepartment")}</option>
            {DEPARTMENT_IDS.map((id) => (
              <option key={id} value={id}>
                {workspaceLabel(id)}
              </option>
            ))}
          </select>
        </label>
        <label className="subject-uuid">
          <span className="subject-label">{t("team.reason")}</span>
          <input type="text" value={reason} onChange={(event) => setReason(event.target.value)} />
        </label>
        <button type="submit" className="btn primary" disabled={save.pending}>
          {t("roles.new.create")}
        </button>
        {save.problem ? <span className="subject-problem">{save.problem}</span> : null}
        {notice ? <span className="meta">{notice}</span> : null}
      </div>

      <p className="meta">{t("roles.new.grantsNeeded")}</p>
      <div className="grant-split">
        <div className="grant-picks">
          {groups().map((group) => (
            <fieldset key={group.category} className="grant-group">
              <legend>{categoryName(group.category)}</legend>
              {group.features.map((feature) => (
                <GrantRow
                  key={feature.settingId}
                  feature={feature}
                  draft={draft}
                  onFeature={(on) => {
                    save.edited();
                    setNotice(null);
                    setDraft((current) => withFeature(current, feature.settingId, on));
                  }}
                  onPermission={(permission, on) => {
                    save.edited();
                    setNotice(null);
                    setDraft((current) => withPermission(current, permission, on));
                  }}
                />
              ))}
            </fieldset>
          ))}
        </div>
        <OpensScreens draft={draft} />
      </div>
      {save.error ? <WriteFailureNotice failure={describeFailure(save.error)} onReload={onCreated} /> : null}
    </form>
  );
}

/**
 * What the picks on the left add up to.
 *
 * The consequence of the ticks, not a second thing to set: a screen appears
 * because the role holds what opens it, and the API checks the same features
 * on every action behind it.
 */
function OpensScreens({ draft }: { draft: RoleDraft }) {
  const screens = opensScreens(draft);
  return (
    <aside className="grant-opens">
      <h2>{t("roles.opens")}</h2>
      <p className="meta">{t("roles.opens.explain")}</p>
      <ul>
        {screens.map(({ screen, opens }) => (
          <li key={screen.id} className={opens ? "on" : undefined}>
            <span aria-hidden="true">{opens ? "\u2713" : "\u2014"}</span>
            <span>{t(screen.label)}</span>
          </li>
        ))}
      </ul>
    </aside>
  );
}

/** Why a box cannot be ticked, in words rather than a silently dead control. */
function lockReason(lock: CellLock | PermissionLock): string | null {
  switch (lock) {
    case null:
      return null;
    case "readOnly":
      return t("roles.lock.readOnly");
    case "ownRole":
      // Unreachable for a role being created — it is held by nobody — but the
      // switch is exhaustive so this cannot go stale if the rule changes.
      return t("roles.lock.readOnly");
    case "critical":
      return t("roles.lock.critical");
    case "high":
      return t("roles.lock.high");
    case "featureFirst":
      return t("roles.lock.featureFirst");
  }
}

/** One feature, and the permissions that stand on it (ADR-0019). */
function GrantRow(props: {
  feature: GridFeature;
  draft: RoleDraft;
  onFeature: (on: boolean) => void;
  onPermission: (permission: GridPermission, on: boolean) => void;
}) {
  const { feature, draft, onFeature, onPermission } = props;
  const { me } = useStaff();
  const lock = lockOf(me, feature);
  const locked = lockReason(lock);
  const permissions = permissionsOf(feature.settingId);

  return (
    <div className="grant-row">
      <label>
        <input
          type="checkbox"
          checked={hasFeature(draft, feature.settingId)}
          disabled={locked !== null}
          onChange={(event) => onFeature(event.target.checked)}
        />
        <span>{featureName(feature.settingId)}</span>
        {locked ? <span className="value-note locked">{locked}</span> : null}
      </label>
      {permissions.map((permission) => {
        const permissionLocked = lockReason(permissionLockOf(me, permission));
        return (
          <label key={permission.settingId} className="grant-permission">
            <input
              type="checkbox"
              checked={hasPermission(draft, permission.settingId)}
              disabled={permissionLocked !== null}
              onChange={(event) => onPermission(permission, event.target.checked)}
            />
            <span>{permissionName(permission.settingId)}</span>
            {permissionLocked ? <span className="value-note locked">{permissionLocked}</span> : null}
          </label>
        );
      })}
    </div>
  );
}
